import { useCreateContractUtilizationFeedbackMutation } from '@cartwheel/api/v1'
import { Button, Checkbox, Modal, useToast } from '@cartwheel/components'
import { useState } from 'react'

interface FeedbackOption {
  id: string
  label: string
}

const POSITIVE_OPTIONS: FeedbackOption[] = [
  { id: 'clarity_pro_bono', label: 'Clarity on pro bono spots' },
  { id: 'quick_view', label: 'Quick view of how my district is performing' },
  { id: 'useful_for_planning_budgeting', label: 'Useful for planning and budgeting' },
  { id: 'other', label: 'Other' },
]

const NEGATIVE_OPTIONS: FeedbackOption[] = [
  { id: 'not_relevant', label: "This information isn't relevant to me" },
  { id: 'dont_understand', label: "I don't understand what these numbers mean" },
  { id: 'need_different_metrics', label: 'I need different metrics not shown here' },
  { id: 'inaccurate', label: "The numbers don't look accurate" },
  { id: 'other', label: 'Other' },
]

export type FeedbackSentiment = 'positive' | 'negative'

export interface FeedbackModalProps {
  isOpen: boolean
  sentiment: FeedbackSentiment
  onSentimentChange: (sentiment: FeedbackSentiment) => void
  onClose: () => void
  onSubmitted: () => void
}

export function FeedbackModal({
  isOpen,
  sentiment,
  onSentimentChange,
  onClose,
  onSubmitted,
}: FeedbackModalProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [otherText, setOtherText] = useState('')
  const toast = useToast()

  const submitFeedbackMutation = useCreateContractUtilizationFeedbackMutation({
    onSuccess: () => {
      toast.success({ description: 'Thank you for your feedback!' })
      onSubmitted()
    },
  })

  const options = sentiment === 'positive' ? POSITIVE_OPTIONS : NEGATIVE_OPTIONS
  const subtitle = `What made this ${sentiment === 'positive' ? 'helpful' : 'unhelpful'}? (select all that apply)`
  const handleSentimentChange = (newSentiment: FeedbackSentiment) => {
    if (newSentiment !== sentiment) {
      setSelected(new Set())
      setOtherText('')
      onSentimentChange(newSentiment)
    }
  }

  const toggleOption = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const otherRequiresFill = selected.has('other') && otherText.trim() === ''
  const canSubmit = selected.size > 0 && !otherRequiresFill && !submitFeedbackMutation.isPending

  const handleSubmit = () => {
    submitFeedbackMutation.mutate({
      body: {
        helpful: sentiment === 'positive',
        reasons: Array.from(selected),
        other_text: selected.has('other') ? otherText : null,
      },
    })
  }

  const handleClose = () => {
    setSelected(new Set())
    setOtherText('')
    onClose()
  }

  const isPositive = sentiment === 'positive'

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Tell us more">
      <p className="text-sm text-charcoal-50 mb-4">{subtitle}</p>

      <div className="mb-6 flex rounded-lg bg-charcoal-25/20 p-1">
        <button
          type="button"
          onClick={() => handleSentimentChange('positive')}
          className={`flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-all ${
            isPositive ? 'bg-white text-charcoal shadow-sm' : 'text-charcoal-50 hover:text-charcoal'
          }`}
        >
          <i className="fa-regular fa-thumbs-up" aria-hidden="true" />
          Positive
        </button>
        <button
          type="button"
          onClick={() => handleSentimentChange('negative')}
          className={`flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-all ${
            isPositive ? 'text-charcoal-50 hover:text-charcoal' : 'bg-white text-charcoal shadow-sm'
          }`}
        >
          <i className="fa-regular fa-thumbs-down" aria-hidden="true" />
          Negative
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {options.map((option) => {
          const isSelected = selected.has(option.id)
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => toggleOption(option.id)}
              className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm text-charcoal transition-colors ${
                isSelected
                  ? 'border-sea bg-sky-25'
                  : 'border-charcoal-25 bg-white hover:border-charcoal-25/60'
              }`}
            >
              <Checkbox
                checked={isSelected}
                className="shrink-0 pointer-events-none data-[checked]:bg-sea data-[checked]:border-sea"
              />
              {option.label}
            </button>
          )
        })}

        {selected.has('other') && (
          <div>
            <p className="mb-1 text-sm font-semibold text-charcoal">
              Please specify<span className="text-red-500">*</span>
            </p>
            <textarea
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder="Tell us more..."
              className="w-full rounded-lg border border-charcoal-25/40 p-3 text-sm focus:border-forest focus:ring-2 focus:ring-forest/20 focus:outline-none"
              rows={3}
            />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6">
        <span className="text-xs text-charcoal-50">
          {selected.size} item{selected.size !== 1 ? 's' : ''} selected
        </span>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            Submit
          </Button>
        </div>
      </div>
    </Modal>
  )
}
